<template>
  <div
    v-if="state"
    class="san-refinement-selector"
    :class="[options.length === 0 && 'no-refinement']"
  >
    <slot
      :options="options"
      :refine="refine"
      :search-for-items="state.searchForItems"
      :search-for-items-query="searchForFacetValuesQuery"
      :toggle-show-more="toggleShowMore"
      :can-toggle-show-more="state.canToggleShowMore"
      :is-showing-more="state.isShowingMore"
      :create-url="state.createUrl"
      :is-from-search="state.isFromSearch"
      :can-refine="state.canRefine"
      :send-event="state.sendEvent"
    >
      <slot
        v-if="state.isFromSearch && options.length === 0"
        name="noResults"
        :query="searchForFacetValues"
      >
        <div class="san-refinement-selector--no-results">
          No results.
        </div>
      </slot>

      <!-- @filter="onFilter"
        @add="onAdd" -->
      <!-- v-model="selected" -->
      <SanFieldMultiselect
        v-if="options.length > 0"
        ref="multiselectRef"
        :model-value="selected"
        class="san-refinement-selector--select"
        :options="options"
        :option-label="optionLabel"
        :option-value="optionValue"
        :placeholder="placeholder"
        emit-value
        map-options
        hide-bottom-space
        @add="({ value }) => refine(value)"
        @remove="({ value }) => refine(value)"
      >
        <!-- @update:model-value="val => refine(val)" -->
        <template #option="{ itemProps, opt }">
          <slot
            name="item"
            :item="opt"
            :refine="refine"
            :create-url="state.createUrl"
          >
            <q-item
              v-bind="itemProps"
              class="san-refinement-selector--option"
            >
              <!-- {{ console.log(opt) }} -->
              <q-item-section
                v-if="opt.avatar"
                class="san-refinement-selector--option--avatar"
                avatar
              >
                <!-- <q-icon name="img:~assets/flags/hr.svg" /> -->
                <q-icon
                  class="text-dark"
                  size="sm"
                >
                  <img :src="opt.avatar">
                </q-icon>
              </q-item-section>
              <q-item-section class="san-refinement-selector--option--label">
                <q-item-label v-if="searchable">
                  <ais-highlight
                    attribute="item"
                    :hit="opt"
                  />
                </q-item-label>
                <q-item-label v-else>
                  {{ opt.label }}
                </q-item-label>
              </q-item-section>
              <q-item-section
                side
                class="san-refinement-selector--option--count"
              >
                <q-badge
                  color="teal"
                  :label="opt.count"
                />
              </q-item-section>
            </q-item>
          </slot>
        </template>
      </SanFieldMultiselect>

      <button
        v-if="showMore"
        class="san-refinement-selector--show-more"
        :class="[!state.canToggleShowMore && 'san-refinement-selector--show-more--disabled']"
        :disabled="!state.canToggleShowMore"
        @click="toggleShowMore"
      >
        <slot
          name="showMoreLabel"
          :is-showing-more="state.isShowingMore"
        >
          Show {{ state.isShowingMore ? 'less' : 'more' }}
        </slot>
      </button>
    </slot>
  </div>
</template>

<script setup>
import { inject, ref, computed, watch } from 'vue'
import { useConnector } from '@/composables'
import { connectRefinementList } from 'instantsearch.js/es/connectors'
// import SearchInput from 'vue-instantsearch/src/components/SearchInput.vue'

defineOptions({ name: 'SanRefinementSelector' })
const props = defineProps({
  attribute: { type: String, required: true },
  searchable: { type: Boolean, default: undefined },
  searchablePlaceholder: { type: String, required: false, default: 'Search here…' },
  operator: { default: 'or', validator (value) {
    return value === 'and' || value === 'or'
  }, required: false },
  limit: { type: Number, required: false, default: undefined },
  showMoreLimit: { type: Number, required: false, default: undefined },
  showMore: { type: Boolean, required: false, default: false },
  sortBy: { type: [Array, Function], required: false, default: undefined },
  transformItems: { type: Function, required: false, default: undefined },
  placeholder: { type: String, default: null },
  optionLabel: { type: String, default: 'label' },
  optionValue: { type: String, default: 'value' }
})

const $emitter = inject('instantSearchPanelEmitter', { emit: () => {} })

const noop = () => {}

const { state } = useConnector({
  connector: connectRefinementList,
  props: {
    $$widgetType: 'ais.refinementList',
    attribute: props.attribute,
    operator: props.operator,
    limit: props.limit,
    showMore: props.showMore,
    showMoreLimit: props.showMoreLimit,
    sortBy: props.sortBy,
    escapeFacetValues: true,
    transformItems: props.transformItems
  }
})

const multiselectRef = ref()
const searchForFacetValuesQuery = ref('')
const hasEmitted = ref(false)
// const selected = ref([])

const selected = computed(() => {
  return state.value.items.filter(i => i.isRefined).map(i => i.value)
})

const searchForFacetValues = computed({
  get: () => {
    return searchForFacetValuesQuery.value
  },
  set: value => {
    state.value.searchForItems(value)
    searchForFacetValuesQuery.value = value
  }
})

const toggleShowMore = computed(() => {
  return state.value.toggleShowMore || noop
})

const options = computed(() => {
  const data = state.value.items.map(item =>
    Object.assign({}, item, {
      _highlightResult: {
        item: {
          value: item.highlighted
        }
      }
    })
  )
  return data
})
// console.log(options)
const refine = value => {
  // console.log(state.value)
  state.value.refine(value)
  searchForFacetValuesQuery.value = ''
  // console.log('%cselected', 'color:magenta', selected.value)
}

const mapStateToCanRefine = s => !!s.canRefine

watch(() => state.value, (newState, oldState) => {
  if (!newState) return

  const previousCanRefine = mapStateToCanRefine(oldState || {})
  const nextCanRefine = mapStateToCanRefine(newState)

  // console.log('%cstate', 'color:orange', newState.items.filter(i => i.isRefined).map(i => i.value))

  if (!hasEmitted.value || previousCanRefine !== nextCanRefine) {
    $emitter.emit('PANEL_CHANGE_EVENT', nextCanRefine)
    hasEmitted.value = true
  }
}, { immediate: true })

watch(() => state.value, (newState, oldState) => {
  if (!newState) return

  const previousCanRefine = mapStateToCanRefine(oldState || {})
  const nextCanRefine = mapStateToCanRefine(newState)

  // console.log('%cstate', 'color:orange', newState.items.filter(i => i.isRefined).map(i => i.value))

  if (!hasEmitted.value || previousCanRefine !== nextCanRefine) {
    $emitter.emit('PANEL_CHANGE_EVENT', nextCanRefine)
    hasEmitted.value = true
  }
}, { immediate: true })
</script>
